import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './lib/supabase';
import LanguageContext from './LanguageContext';
import { homePageCopy, translateService } from './translations';
import { formatPrice } from './utils/priceFormatter';
import './App.css';

function Home() {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { language } = useContext(LanguageContext);
  const copy = homePageCopy[language] || homePageCopy.English;

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(6); // Show only first 6 services on home page

      if (error) {
        throw error;
      }

      setServices(data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="wave-container">
          <svg className="wave wave-bottom" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="url(#gradient1)" d="M0,96L48,112C96,128,192,160,288,165.3C384,171,480,149,576,133.3C672,117,768,107,864,112C960,117,1056,139,1152,149.3C1248,160,1344,160,1392,160L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#20B2AA" stopOpacity="0.8"/>
                <stop offset="100%" stopColor="#008B8B" stopOpacity="0.9"/>
              </linearGradient>
            </defs>
          </svg>
          <svg className="wave wave-middle" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="url(#gradient2)" d="M0,160L48,170.7C96,181,192,203,288,208C384,213,480,203,576,186.7C672,171,768,149,864,154.7C960,160,1056,192,1152,197.3C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            <defs>
              <linearGradient id="gradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#1E3A8A" stopOpacity="0.7"/>
                <stop offset="100%" stopColor="#1E40AF" stopOpacity="0.8"/>
              </linearGradient>
            </defs>
          </svg>
          <svg className="wave wave-top" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="url(#gradient3)" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,213.3C672,224,768,224,864,208C960,192,1056,160,1152,154.7C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
            <defs>
              <linearGradient id="gradient3" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.6"/>
                <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.7"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="sun-moon-glow"></div>
        <div className="hero-content">
          <h1 className="brand-name">
            <span className="mana-text">Mana</span>
            <span className="wave-text">Wave</span>
          </h1>
          <p className="tagline">{copy.heroTagline}</p>
          <div className="hero-cta">
            <Link to="/services" className="btn-primary">{copy.heroPrimary}</Link>
            <Link to="/services" className="btn-secondary">{copy.heroSecondary}</Link>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="intro-section">
        <div className="container">
          <div className="intro-content-wrapper">
            <div className="intro-image-container">
              <img 
                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80" 
                alt="Spiritual wellness and meditation" 
                className="intro-image"
              />
            </div>
            <div className="section-content">
              <h2 className="section-title">{copy.intro.title}</h2>
              <p className="section-text">
                {copy.intro.text}
              </p>
            </div>
          </div>
        </div>
        <div className="wave-separator wave-separator-1"></div>
      </section>

      {/* What Is Reiki Section */}
      <section className="reiki-section">
        <div className="container">
          <div className="card">
            <div className="reiki-image-container">
              <img 
                src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80" 
                alt="Spiritual energy flow" 
                className="reiki-image"
              />
            </div>
            <h2 className="section-title">{copy.reiki.title}</h2>
            <p className="section-text">
              {copy.reiki.text}
            </p>
          </div>
        </div>
        <div className="wave-separator wave-separator-2"></div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <h2 className="section-title">{copy.howItWorks.title}</h2>
          <div className="steps-grid">
            {copy.howItWorks.steps.map((step, index) => (
              <div key={step.title} className="step-card">
                <div className="step-icon">{index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="wave-separator wave-separator-3"></div>
      </section>

      {/* Services Preview Section */}
      <section className="services-section">
        <div className="container">
          <h2 className="section-title">{copy.services.title}</h2>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>{copy.services.loading}</p>
            </div>
          ) : services.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>{copy.services.empty}</p>
            </div>
          ) : (
            <>
              <div className="services-grid">
                {services.map((service) => {
                  const translatedService = translateService(service, language);
                  return (
                    <Link
                      key={service.id}
                      to={`/services/${service.id}`}
                      className="service-card"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <img
                        src={service.image || 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&q=80'}
                        alt={translatedService.name}
                        className="service-image"
                      />
                      <h3>{translatedService.name}</h3>
                      <p className="service-duration">{translatedService.duration}</p>
                      <p className="service-price">{formatPrice(translatedService.price)}</p>
                    </Link>
                  );
                })}
              </div>
          <Link to="/services" className="btn-view-all">{copy.services.viewAll}</Link>
            </>
          )}
        </div>
        <div className="wave-separator wave-separator-4"></div>
      </section>

      {/* Why Try Energy Work Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="section-title">{copy.benefits.title}</h2>
          <div className="benefits-list">
            {copy.benefits.items.map((benefit, index) => (
              <React.Fragment key={benefit.title}>
                <div className="benefit-item">
                  <div className="benefit-glow"></div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.text}</p>
                </div>
                {index < copy.benefits.items.length - 1 && (
                  <div className="benefit-separator"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="wave-separator wave-separator-5"></div>
      </section>

      {/* Guided & Remote Sessions Section */}
      <section className="sessions-section">
        <div className="container">
          <h2 className="section-title">{copy.sessions.title}</h2>
          <div className="sessions-grid">
            {copy.sessions.cards.map((card) => (
              <div key={card.title} className="session-type-card">
                <img 
                  src={card.image} 
                  alt={card.alt} 
                  className="session-image"
                />
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="wave-separator wave-separator-6"></div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title">{copy.testimonials.title}</h2>
          <div className="testimonials-grid">
            {copy.testimonials.items.map((item) => (
              <div key={item.text} className="testimonial-card">
                <div className="testimonial-blur"></div>
                <p className="testimonial-text">{item.text}</p>
                <p className="testimonial-author">{item.author}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="wave-separator wave-separator-7"></div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">{copy.cta.title}</h2>
          <p className="cta-text">{copy.cta.text}</p>
          <Link to="/services" className="btn-cta">{copy.cta.button}</Link>
        </div>
      </section>

      {/* Our Partners Section */}
      <section className="partners-section">
        <div className="container">
          <h2 className="section-title">{copy.partners.title}</h2>
          <div className="partners-container">
            <a 
              href="https://lifeforcedynamics.com/shop" 
              target="_blank" 
              rel="noopener noreferrer"
              className="partner-logo-link"
            >
              <img 
                src="https://snkthiiudjitcfuyliuw.supabase.co/storage/v1/object/public/logo/WhatsApp%20Image%202026-01-21%20at%2011.55.29%20AM.jpeg" 
                alt="Life Force Dynamics Partner Logo" 
                className="partner-logo"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="footer-section">
        <div className="container">
          <div className="footer-content" style={{ textAlign: 'center' }}>
            <div className="footer-column">
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>Address: 1st Floor, 124 Cleveland Street, London W1T 6PG</p>
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>
                <a href="mailto:manawave@manawave.uk" style={{ color: '#C7D2FE', textDecoration: 'none' }}>manawave@manawave.uk</a>
              </p>
              <p style={{ color: '#C7D2FE', marginBottom: '0.5rem' }}>
                <a href="tel:+447356083797" style={{ color: '#C7D2FE', textDecoration: 'none' }}>+447356083797</a>
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>Copyright © 2026 ManaWave LTD All Rights Reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;

